<template>
  <DialogContent class="flex flex-col items-center relative flex-1">
    <DacrBadge class="size-10" :icon-size="28" />

    <div
      class="font-primary font-semibold text-[34px] leading-[50px] text-gray-800 mb-4"
    >
      {{ headline }}
    </div>

    <Subheadline />

    <div class="mb-7">
      <BundleInput
        v-if="currentPlan"
        v-model="selectedItemProductId"
        :items="currentPlan.items"
      />

      <Skeleton v-else class="w-[232px] h-[50px]" />
    </div>

    <div
      v-if="currentPlan"
      class="flex items-center font-secondary text-[13px] leading-[18px] text-gray-400"
    >
      <template v-if="isMonthlyBillingCycle">
        Switch to <span class="font-medium ml-1">yearly and save!</span>
      </template>

      <div v-else class="font-medium">Switch to monthly billing</div>

      <Toggle v-model="switchBillingCycle" class="ml-2.5" />
    </div>

    <Skeleton v-else class="w-[215px] h-5" />

    <div class="w-full mt-auto">
      <div class="w-full h-px bg-gray-800/10 mt-5 mb-[19px]"></div>

      <Message v-if="showCallout" icon="Coin" type="success" class="mb-4">
        {{ calloutMessage }}
        <span class="text-link" @click="handleContactUsClick">
          Contact us
        </span>
      </Message>

      <Message v-if="showIAPCallout" icon="Alert" type="warning" class="mb-4">
        You've reached the limit {{ platform }} allows for subscriptions. To
        upgrade your plan further, you will need to cancel your subscription on
        {{ platform }} and resubscribe on Dacr.com.
      </Message>

      <div class="w-full flex items-end justify-between">
        <div>
          <div class="text-gray-400 leading-[18px] mb-2">You'll now pay</div>
          <div v-if="currentPlan" class="flex items-baseline">
            <div
              class="font-primary font-semibold text-[34px] leading-[38px] text-gray-800 mr-1"
            >
              {{ price }}
            </div>
            <div
              class="font-secondary font-medium text-[13px] leading-[16px] text-gray-800"
            >
              /{{ renderedBillingCycle }}
            </div>
          </div>
          <Skeleton v-else class="w-[115px] h-[38px]" />
        </div>
        <div class="flex items-center">
          <RouterLink
            to="/change-plans"
            class="font-secondary font-medium text-[16px] leading-[18px] text-green-700 mr-[26px]"
            @click="close()"
          >
            See plans
          </RouterLink>
          <UiButton
            primary
            large
            :disabled="disableButton"
            @click="handleUpgradeClick"
          >
            Upgrade
          </UiButton>
        </div>
      </div>
    </div>
  </DialogContent>
</template>

<script setup lang="ts">
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import {
  PlanItemStatus,
  usePlansQueryQuery,
  type Plan,
  type PlanItem,
  type PlanResponse,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { useUserStore } from '@/shared/stores/user'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { SelectPlanType } from '@/shared/types'
import { capitalize } from '@/shared/utils/string'
import { getTokens } from '@dacr/auth'
import { Skeleton, Toggle, UiButton, Message } from '@dacr/ui'
import { computed, h, ref, watch, watchEffect } from 'vue'
import { useRouter } from 'vue-router'
import BundleInput from '@/features/billing/components/BundleInput.vue'
import DacrBadge from '@/shared/components/DacrBadge.vue'

const FILES_THRESHOLD = 5000

const props = defineProps<{
  willReachLimit?: boolean
  hasRachedLimit?: boolean
}>()

const { close, open } = useDialog()

const router = useRouter()
const billingStore = useBillingStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const { result } = usePlansQueryQuery(
  {
    accessToken: getTokens().accessToken,
  },
  {
    enabled: !webviewStore.isActive,
  }
)

const billingCycle = ref('')
const plans = ref<PlanResponse>()
const selectedItemProductId = ref('')
const selectedItemMaxUnits = ref<number>()

watchEffect(() => {
  plans.value = result.value?.plans || billingStore.webviewPlans
  billingCycle.value =
    result.value?.plans?.activeBillingCycleType ||
    billingStore.webviewPlans?.activeBillingCycleType ||
    ''
})

watch(selectedItemProductId, () => {
  if (!selectedItemProductId.value || !selectedItem.value) return

  selectedItemMaxUnits.value = selectedItem.value.maxUnits || 0
})

const headline = computed(() => {
  if (props.hasRachedLimit) {
    return 'Upgrade to add more'
  }

  return 'Upgrade your limit'
})

const Subheadline = computed(() => {
  if (props.willReachLimit) {
    return h(
      'div',
      {
        class: 'text-[16px] leading-[24px] mb-[37px] text-center',
      },
      [
        h(
          'div',
          {
            class: 'font-primary font-medium text-gray-800',
          },
          'Before you reach the limit of your current plan,'
        ),
        h(
          'div',
          {
            class: 'font-secondary text-gray-500',
          },
          'upgrade your copyright limit or change to a pay-per-copyright plan to continue uploading your work.'
        ),
      ]
    )
  }

  if (props.hasRachedLimit) {
    return h(
      'div',
      {
        class: 'text-[16px] leading-[24px] mb-[37px] text-center',
      },
      [
        h(
          'div',
          {
            class: 'font-primary font-medium text-gray-800',
          },
          'You\u2019ve reached the limit of your current plan.'
        ),
        h(
          'div',
          {
            class: 'font-secondary text-gray-500',
          },
          'You\u2019ve reached the limit of your current plan.'
        ),
      ]
    )
  }

  return h(
    'div',
    {
      class:
        'font-secondary text-gray-500 text-[16px] leading-[24px] mb-[37px] text-center',
    },
    [
      h(
        'span',
        {
          class: 'font-primary font-medium text-gray-800 mr-1',
        },
        'Protect more of your work.'
      ),
      'Upgrade your copyright limit to upload even more.',
    ]
  )
})

const isMonthlyBillingCycle = computed(
  () => plans.value?.activeBillingCycleType === 'month'
)

const renderedBillingCycle = computed(() =>
  webviewStore.isActive ? billingCycle.value : 'month'
)

const switchBillingCycle = computed({
  get() {
    return billingCycle.value !== plans.value?.activeBillingCycleType
  },
  set(value) {
    if (!plans.value?.activeBillingCycleType) return

    billingCycle.value = value
      ? plans.value.activeBillingCycleType === 'month'
        ? 'year'
        : 'month'
      : plans.value.activeBillingCycleType
  },
})

const platform = computed(() =>
  capitalize(billingStore.billing?.plan.platform || '')
)

const billingCycleGroup = computed(() =>
  plans.value?.groupByBillingCycle.find(
    (billingPeriod) => billingPeriod?.billingCycleType === billingCycle.value
  )
)

const currentPlan = computed(() => {
  const plan = billingCycleGroup.value?.plans.find(
    (plan) => plan.items.length > 1
  )

  if (!plan) return

  const items = plan.items.filter(
    (item, index) =>
      item.status === PlanItemStatus.Active || index === plan.items.length - 1
  )

  return {
    ...plan,
    items,
  }
})

const selectedItem = computed(() =>
  currentPlan.value?.items.find(
    (item) => item.productId === selectedItemProductId.value
  )
)

const lastItem = computed(
  () => currentPlan.value?.items[currentPlan.value.items.length - 1]
)

const nextAvailablePlanItem = computed(() => {
  const lastItemMaxUnits = lastItem.value?.maxUnits || 0

  if (selectedItemMaxUnits.value) {
    const maxUnits = Math.min(selectedItemMaxUnits.value || 0, lastItemMaxUnits)

    return currentPlan.value?.items.find(
      (item) => (item.maxUnits || 0) >= maxUnits
    )
  }

  if (lastItemMaxUnits <= (userStore.user?.totalFilesLimit || 0)) {
    return lastItem.value
  }

  const maxUnits = Math.min(
    userStore.user?.totalFilesLimit || 0,
    lastItemMaxUnits
  )

  return currentPlan.value?.items.find(
    (item) => (item.maxUnits || 0) > maxUnits
  )
})

watch(
  billingCycle,
  () => {
    selectedItemProductId.value = nextAvailablePlanItem.value?.productId || ''
  },
  {
    immediate: true,
  }
)

const disableButton = computed(() => {
  if (!currentPlan.value) {
    return true
  }

  if (
    lastItem.value?.productId === selectedItemProductId.value &&
    billingStore.billing?.plan.id === lastItem.value.productId
  ) {
    return true
  }

  return false
})

const price = computed(() => {
  return selectedItem.value?.prices[0].details?.renderedPrice
})

const showCallout = computed(
  () =>
    !webviewStore.isActive &&
    ((selectedItem.value?.maxUnits || 0) >= FILES_THRESHOLD ||
      selectedItemProductId.value ===
        currentPlan.value?.items.at(-1)?.productId)
)

const calloutMessage = computed(() => {
  if (
    selectedItemProductId.value === currentPlan.value?.items.at(-1)?.productId
  ) {
    return 'Increase your limit and save big!'
  }

  if ((selectedItem.value?.maxUnits || 0) >= FILES_THRESHOLD) {
    return 'Save big with higher limits!'
  }

  return ''
})

const showIAPCallout = computed(
  () =>
    webviewStore.isActive &&
    currentPlan.value?.items.at(-1)?.productId === selectedItemProductId.value
)

const handleContactUsClick = () => {
  if (webviewStore.isActive) {
    return webviewStore.postMessage({
      type: NativeActionsEnum.ContactUs,
    })
  }

  window.open('https://help.dacr.com/hc/en-us/requests/new', '_blank')
}

const handleUpgradeClick = () => {
  if (webviewStore.isActive) {
    webviewStore.postMessage({
      type: NativeActionsEnum.PlanSelected,
      data: {
        productId: selectedItem.value?.nativeProductId,
        planId: selectedItem.value?.nativePlanId,
      },
    })

    return close()
  }

  if (!billingStore.canUpdateSubscription) {
    return open(Dialogs.IAP, {
      props: {
        type: 'change',
      },
    })
  }

  billingStore.setPlanData({
    plan: currentPlan.value as Plan,
    planItem: selectedItem.value as PlanItem,
    billingCycle: {
      type: billingCycleGroup.value?.billingCycleType || '',
      description: billingCycleGroup.value?.billingCycleDescription || '',
    },
  })

  router.push(`/${SelectPlanType.Change}/checkout`)

  close()
}
</script>

<style scoped>
:global(#bundle-input #iconComponent path) {
  stroke-width: 2.25px !important;
}
</style>
